"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CreditCard, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/context/permissions-context";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { post } from "@/utils/api-handler";
import { useAppSelector } from "@/redux/hooks";
import { usePartnerBranding } from "@/hooks/use-partner-branding";
import { cn } from "@/lib/utils";

interface TrialPlanBannerProps {
  className?: string;
}

export function TrialPlanBanner({ className }: TrialPlanBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const user = useAppSelector((state) => state.user.user);
  const isUserLoading = useAppSelector((state) => state.user.isLoading);
  const { isReady } = usePermissions();
  const { isReferredByPartner } = usePartnerBranding();

  // The link only comes back after the request, and a window opened that late
  // is treated as a popup and blocked. So a tab is opened on the click itself
  // and pointed at the form once the link arrives.
  const [pendingWindow, setPendingWindow] = useState<Window | null>(null);

  const initiatePaymentMethodCreation = useProtectedMutation({
    requiredPermissions: ["ADD_BILLING_PAYMENT_METHOD"],
    mutationFn: () => post("/api/billing/payment-methods/initiate-creation", {}),
    onSuccess: (response: any) => {
      const redirectUrl = response?.data?.redirect_url ?? response?.redirect_url;

      if (redirectUrl) {
        if (pendingWindow && !pendingWindow.closed) {
          pendingWindow.location.href = redirectUrl;
        } else {
          window.location.href = redirectUrl;
        }
        setPendingWindow(null);
        return;
      }

      pendingWindow?.close();
      setPendingWindow(null);
      toast.error("Couldn't open the payment form", {
        description: "No payment link was returned. Please try again.",
      });
    },
    onError: (error: any) => {
      pendingWindow?.close();
      setPendingWindow(null);
      if (error?.message === "PERMISSION_DENIED") return;

      const message =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message ??
        "Please try again.";
      toast.error("Couldn't start adding a payment method", { description: message });
    },
  });

  const hasPaymentMethod =
    (user?.organization?.billing_preference?.payment_methods?.length ?? 0) > 0;

  // Nothing is known until the session resolves, so staying hidden avoids the
  // banner snapping in and pushing the page down a moment after it paints.
  const isVisible =
    isReady &&
    !isDismissed &&
    !isUserLoading &&
    Boolean(user) &&
    !hasPaymentMethod &&
    !isReferredByPartner;

  const isPending = initiatePaymentMethodCreation.isPending;

  return (
    <AnimatePresence initial={false}>
      {isVisible ? (
        <motion.div
          key="trial-plan-banner"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
    <div className={cn("relative mx-auto my-[10px] flex w-[calc(100%-1.5rem)] max-w-[648px] shrink-0 flex-col gap-4 overflow-hidden rounded-[20px] bg-gradient-to-r from-[#2A2092] to-[#6B30FF] py-4 pl-4 pr-12 text-white shadow-lg shadow-violet-950/15 sm:w-[calc(100%-2rem)] sm:pl-[22px] sm:pr-14 lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:py-3.5 lg:pl-[27px] lg:pr-14", className)}>
      <button
        type="button"
        onClick={() => setIsDismissed(true)}
        aria-label="Dismiss"
        className="absolute right-3 top-3 flex size-7 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white"
      >
        <X className="size-4" aria-hidden />
      </button>

      <div className="min-w-0 lg:flex-1">
        <div className="inline-flex w-fit max-w-full shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold leading-none text-[#6B30FF] sm:text-[12px]">
          <CreditCard className="size-3.5" aria-hidden />
          Payment method required
        </div>

        <p className="mt-2 max-w-[460px] text-sm font-normal tracking-[-0.01em] text-white/80 lg:leading-[1.2]">
          Payment information is missing. Add your payment details to start using AI operations.
        </p>
      </div>

      <Button
        type="button"
        size="sm"
        disabled={isPending}
        onClick={() => {
          // Without a handle back there is nothing to point at the form later,
          // so `noopener` is dropped and the reference severed by hand instead.
          const tab = window.open("about:blank", "_blank");
          if (tab) tab.opener = null;
          setPendingWindow(tab);
          initiatePaymentMethodCreation.mutate();
        }}
        className="h-[41px] shrink-0 self-start whitespace-nowrap rounded-full bg-[#FFFFFF] px-5 text-[14px] font-bold leading-[17px] text-[#2A2092] hover:bg-[#FFFFFF]/90 disabled:opacity-70 lg:self-center"
      >
        {isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Opening…
          </>
        ) : (
          <>
            Add payment method
            <ArrowRight className="size-4" aria-hidden />
          </>
        )}
      </Button>
    </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
